/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, useCallback, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import debounce from 'lodash.debounce';
import {translate} from '@ion/core/src/locale/locale';
import {DeviceFile} from '@ion/core/src/types/entities';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {WarningIcon} from '@ion/dls/src/icons/WarningIcon';
import Render from '@ion/dls/src/render/Render';
import {IconSize} from '@ion/dls/src/types/types';
import {useDeviceFiles} from '@ion/query/src/hooks/useDeviceFiles';
import {
  CONFIG,
  DEFAULT_DEVICE_TYPES,
  getFileWarningMessages,
  onRenderFileNameOption
} from './helpers';
import {JobTaskFileNameChooserProps} from './JobTaskFileNameChooser';
import {LMUConfigSearchField} from './JobTaskLMUConfigUtils';
import JobTaskSearchInput from './JobTaskSearchInput';
import {useJobTaskChooserStyles} from './useJobTaskChooserStyles';
import {useJobTaskLMUConfigLists} from './useJobTaskLMUConfigLists';

// defines the component style definitions ...
const useStyles = makeStyles(() => ({
  container : {
    minWidth   : '260px',
    display    : 'flex',
    alignItems : 'flex-end'
  },
  icon : {
    borderBottom : '1px solid #bdbdbd',
    paddingLeft  : '5px',
    cursor       : 'pointer',
    position     : 'absolute',
    zIndex       : 999
  },
  dropdownField : {
    flex                       : 1,
    '& .MuiAutocomplete-input' : {
      paddingLeft : '24px !important'
    }
  },
  tooltipiList : {
    paddingLeft : 20,
    textAlign   : 'left'
  },
  tooltipiListItem : {
    paddingBottom : 5
  }
}));

/**
 * Defines the `JobTaskLMUConfigSearch` prop type definitions, which simply
 * extend the `JobTaskFileNameChooserProps` while requiring an additional
 * `field` property which indicates the field on which the search is to be
 * based.
 *
 * @see {@link JobTaskFileNameChooserProps}
 */
export type JobTaskLMUConfigSearchProps = JobTaskFileNameChooserProps & {
  field: LMUConfigSearchField;
};

/**
 * @internal
 *
 * The `JobTaskLMUConfigSearch` component provides the UI which allows users to
 * perform external Custom Searches for LMU Config Device Files by `version` or
 * `name`. This component provides the Search / AutoComplete / Dropdown Options
 * portion of the LMU Config Chooser Feature.
 *
 * Note, this component is generally not to be used directly, but rather, it will
 * be loaded via the `JobTaskSearchChooser` when the given Task is for an LMU
 * Config.
 *
 * @see {@link JobTaskSearchChooser}
 * @see {@link JobTaskLMUConfigSearchProps}
 * @see {@link JobTaskFileNameChooserProps}
 *
 */
const JobTaskLMUConfigSearch = ({
  row,
  field,
  onChange,
  accountId,
  includeSubAccounts,
  devicePlatformIds,
  isDevicesSelected
}: JobTaskLMUConfigSearchProps) => {
  const classes    = useStyles();
  const styles     = useJobTaskChooserStyles();
  const {task}     = row || {};
  const usesAppIds = task?.usesAppIds;
  const appIds     = task?.appId?.length ? [task.appId] : task.deviceAppIds;
  const isVbus     = DEFAULT_DEVICE_TYPES?.includes(task?.deviceType) && task?.fileType !== CONFIG;

  const [searchTerm, setSearchTerm] = useState('');

  const warningMessage = getFileWarningMessages(task);

  // provides the search parameters and reference to the search query api from
  // which all search requests are to based. by default, LMU Config Device Files
  // are pre-fetched via the `useLMUConfigDeviceFilePrefetch` hook, thus, when
  // this hook is invoked the device files will immediately be available ...
  const {
    deviceFiles,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useDeviceFiles({
    platformIds       : [task?.platformId],
    enabledOnly       : true,
    enabled           : !!task?.deviceType,
    ...(usesAppIds && {appIds}),
    devicePlatformIds : isVbus ? devicePlatformIds : [],
    includeSubAccounts,
    accountId,
    field,
    searchTerm
  });

  /**
   * Provides a utility that used to determine if an option is selected, considering
   * the current value ...
   */
  const onGetSelectedOption = useCallback((option, value) => option?.name === value?.name, []);

  // provides a utility which returns the label to be displayed for the selected
  // option based ...
  const getOptionLabel = useCallback((file: DeviceFile): string => (
    file?.name || ''
  ), []);

  const {
    JobTaskLMUConfigLists
  } = useJobTaskLMUConfigLists({
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  });

  const debounceFieldChange = useCallback(debounce((target) => {
    setSearchTerm(target?.value || '');
  }, 1000), [field]);
  
  // invoked when a textfield change is made, delegating the debounce change handler ...
  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>, value) => {
    debounceFieldChange(e?.target, value);
  }, [debounceFieldChange]);

  const isDisabled =
    !isDevicesSelected ||
    !task?.deviceType ||
    !task?.fileType ||
    isLoading;

  return (
    <div className={classes.container}>
      <Render.If condition={warningMessage?.length > 0}>
        <Tooltip arrow
          title={
            <Render>
              <Render.If condition={warningMessage?.length > 1}>
                <ul className={classes.tooltipiList}>
                  {warningMessage?.map((message, index) => (
                    <li key={index} className={classes.tooltipiListItem}>
                      <span>{message}</span>
                    </li>
                  ))}
                </ul>
              </Render.If>
              <Render.Else>
                <span>{warningMessage[0]}</span>
              </Render.Else>
            </Render>
          }
          className={classes.icon}>
          <span><WarningIcon size={IconSize.small} /></span>
        </Tooltip>
      </Render.If>
      <div className={classes.dropdownField}>
        <Autocomplete classes={styles.dropdownProps}
          onChange={onChange}
          disabled={isDisabled}
          onInputChange={handleInputChange}
          renderOption={onRenderFileNameOption}
          getOptionLabel={getOptionLabel}
          options={deviceFiles}
          value={{name: (searchTerm || task?.fileLabel || task?.metaData?.title)}}
          getOptionSelected={onGetSelectedOption}
          disableClearable
          noOptionsText={translate('jobcreate:search.lmuconfig.noResults')}
          ListboxComponent={JobTaskLMUConfigLists}
          renderInput={function(params) {
            return (
              <JobTaskSearchInput isLoading={isLoading}
                placeholder={`${translate('jobcreate:search.lmuconfig.label')} ${translate(`jobcreate:search.lmuconfig.${field}`)}`}
                params={params} />
            );
          }} />
      </div>
    </div>
  );
};

export default JobTaskLMUConfigSearch;
